import { lazy } from "react";
import { Route, Routes } from "react-router-dom";
import ProtectedRoute from "../components/shared/ProtectedRoute";
import RoleProtectedRoute from "../components/shared/RoleProtectedRoute";
import { ROLES } from "../constants/roles";

// Lazy load seller-specific components
const SellerDashboard = lazy(() => import("../pages/Seller/SellerDashboard"));
const SellerProducts = lazy(() => import("../pages/Seller/SellerProducts"));
const SellerProductCreate = lazy(() => import("../pages/Seller/SellerProductCreate"));
const SellerProductEdit = lazy(() => import("../pages/Seller/SellerProductEdit"));
const SellerMedia = lazy(() => import("../pages/Seller/SellerMedia"));
const SellerNotifications = lazy(() => import("../pages/Seller/SellerNotifications"));
const SellerAdminAlerts = lazy(() => import("../pages/Seller/SellerAdminAlerts"));
const SellerSKUs = lazy(() => import("../pages/Seller/SellerSKUs"));
const SellerSKUEdit = lazy(() => import("../pages/Seller/SellerSKUEdit"));
const SellerSKULogs = lazy(() => import("../pages/Seller/SellerSKULogs"));
const SellerBrands = lazy(() => import("../pages/Seller/SellerBrands"));

// Import existing components that sellers can access
const Products = lazy(() => import("../pages/Product/Product"));
const ProductCreate = lazy(() => import("../pages/Product/ProductCreate"));
const ProductEdit = lazy(() => import("../pages/Product/ProductEdit"));
const Media = lazy(() => import("../pages/Media"));
const Notifications = lazy(() => import("../pages/Notifications"));

/**
 * Seller Routes Configuration
 * Defines all routes accessible to seller role users
 */
export default function SellerRoutes() {
  return (
    <Routes>
      {/* Seller Dashboard */}
      <Route
        path="/dashboard"
        element={
          <RoleProtectedRoute requiredRole={ROLES.SELLER}>
            <SellerDashboard />
          </RoleProtectedRoute>
        }
      />

      {/* Product Management Routes */}
      <Route
        path="/products"
        element={
          <ProtectedRoute permission="view_product">
            <Products />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/products/create"
        element={
          <ProtectedRoute permission="add_product">
            <ProductCreate />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/products/edit/:id"
        element={
          <ProtectedRoute permission="edit_product">
            <ProductEdit />
          </ProtectedRoute>
        }
      />

      {/* Media Management Routes */}
      <Route
        path="/media"
        element={
          <ProtectedRoute permission="view_media">
            <Media />
          </ProtectedRoute>
        }
      />

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute permission="view_notification">
            <Notifications />
          </ProtectedRoute>
        }
      />

      {/* Admin Alerts for Seller */}
      <Route
        path="/admin-alerts"
        element={
          <RoleProtectedRoute requiredRole={ROLES.SELLER}>
            <SellerAdminAlerts />
          </RoleProtectedRoute>
        }
      />

      {/* SKU Management Routes */}
      <Route
        path="/skus"
        element={
          <ProtectedRoute permission="view_sku">
            <SellerSKUs />
          </ProtectedRoute>
        }
      />
      
      {/** Add SKU route is temporarily removed since single SKU API is not available */}
      
      <Route
        path="/skus/edit/:id"
        element={
          <ProtectedRoute permission="edit_sku">
            <SellerSKUEdit />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/skus/:id/logs"
        element={
          <ProtectedRoute permission="view_sku">
            <SellerSKULogs />
          </ProtectedRoute>
        }
      />

      {/* Brand Management Routes */}
      <Route
        path="/brands"
        element={
          <ProtectedRoute permission="view_brand">
            <SellerBrands />
          </ProtectedRoute>
        }
      />

      {/* Redirect any unknown routes to seller dashboard */}
      <Route
        path="*"
        element={
          <RoleProtectedRoute requiredRole={ROLES.SELLER}>
            <SellerDashboard />
          </RoleProtectedRoute>
        }
      />
    </Routes>
  );
}
